import { useState } from 'react';
import axios from 'axios';
import { uuidv4 } from 'common/uuidGenerator';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

const useDownloadProgress = () => {
  const [downloads, setDownloads] = useState([]);
  const { addToast } = useToast();

  const downloadFile = (url, fileName, onComplete, onProgress, addToGlobalList = true, traySuccessOptions) => {
    if (!url) {
      return;
    }

    const id = uuidv4();
    const download = {
      id,
      progress: 0,
      loaded: 0,
      total: 0,
      showProgressBar: true,
      fileName,
      error: null,
      isDismissing: false,
    };

    if (addToGlobalList) {
      setDownloads((prevDownloads) => [...prevDownloads, download]);
    }

    axios({
      url: url,
      method: 'GET',
      responseType: 'blob',
      excludeFromLoading: true,
      onDownloadProgress: (progressEvent) => {
        const totalLength = progressEvent.lengthComputable
          ? progressEvent.total
          : progressEvent.target.getResponseHeader('content-length') ||
            progressEvent.target.getResponseHeader('x-decompressed-content-length');
        if (totalLength) {
          const progress = Math.round((progressEvent.loaded * 100) / totalLength);
          const loaded = progressEvent.loaded;
          const total = Number(totalLength);

          if (addToGlobalList) {
            setDownloads((prevDownloads) => prevDownloads.map((d) => (d.id === id ? { ...d, progress, loaded, total } : d)));
          }

          if (onProgress && typeof onProgress === 'function') {
            onProgress(progress, loaded, total);
          }
        }
      },
    })
      .then((response) => {
        const mediaType = response?.headers['content-type'];
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: mediaType }));

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName || 'no-name');
        document.body.appendChild(link);
        link.click();

        if (addToGlobalList) {
          // Always transition to done phase — show tick for 2s then auto-dismiss
          setDownloads((prevDownloads) =>
            prevDownloads.map((d) =>
              d.id === id
                ? {
                    ...d,
                    progress: 100,
                    showProgressBar: false,
                    downloadSuccess: true,
                    successHeadline: traySuccessOptions?.successHeadline ?? 'Download complete',
                    totalFiles: traySuccessOptions?.totalFiles,
                    isDismissing: false,
                  }
                : d
            )
          );

          // Trigger fade-out animation at 1700ms, remove from state at 2000ms
          setTimeout(() => {
            setDownloads((prevDownloads) => prevDownloads.map((d) => (d.id === id ? { ...d, isDismissing: true } : d)));
          }, 1700);
          setTimeout(() => {
            setDownloads((prevDownloads) => prevDownloads.filter((d) => d.id !== id));
          }, 2000);
        }

        if (onComplete && typeof onComplete === 'function') {
          onComplete();
        }
      })
      .catch((error) => {
        console.error('Error downloading file:', error);

        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, error?.message || 'Failed to download file');

        if (addToGlobalList) {
          setDownloads((prevDownloads) => prevDownloads.map((d) => (d.id === id ? { ...d, showProgressBar: true, error } : d)));
        }

        if (onComplete && typeof onComplete === 'function') {
          onComplete();
        }
      });
  };

  const remove = (id) => {
    setDownloads((prevDownloads) => prevDownloads.filter((download) => download.id !== id));
  };

  return { downloads, downloadFile, remove, setDownloads };
};

export default useDownloadProgress;
